import {
  ExamCenterDetailsInfoBanner,
  transformExamCenterValidationErrors,
} from '@exam-center/exam-center-management';
import {
  GetMyExamCentersDocument,
  OrderType,
  SearchExamCentersDocument,
  useCreateExamCenterMutation,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormButton,
  FormTextField,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';

import { type ExamCenterCreationFormInput, ExamCenterCreationFormSchema } from './schema';

interface ExamCenterCreationModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ExamCenterCreationModal(props: Readonly<ExamCenterCreationModalProps>) {
  const { open: isOpen, onClose } = props;

  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const formContext = useForm<ExamCenterCreationFormInput>({
    resolver: zodResolver(ExamCenterCreationFormSchema),
    defaultValues: {
      nameTH: '',
      nameEN: '',
      code: '',
    },
  });
  const {
    handleSubmit,
    reset,
    setError,
    formState: { isSubmitting },
  } = formContext;

  const [createExamCenter] = useCreateExamCenterMutation();

  const handleCreateExamCenter = async (data: ExamCenterCreationFormInput) => {
    const { nameTH, nameEN, code } = data;

    await createExamCenter({
      variables: {
        createExamCenterInput: {
          nameTH,
          nameEN,
          code,
        },
      },
      refetchQueries: [
        {
          query: SearchExamCentersDocument,
          // TODO: maybe we can pass the same variables from exam-center-list, pending refactor
          variables: {
            searchExamCentersInput: {
              order: {
                field: 'examCenterId',
                type: OrderType.Asc,
              },
              filter: undefined,
              paging: {
                currentPage: 0,
                pageSize: 100, // NOTE: We don't have pagination for exam center list since in production, we only have 3 exam centers
              },
            },
          },
        },
        {
          query: GetMyExamCentersDocument,
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        // TODO: use showPromiseSnackbar instead if needed
        showSnackbar({
          message: 'เพิ่มศูนย์ทดสอบเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
        navigate('/admin/exam-centers/:examCenterCode/details', {
          params: {
            examCenterCode: data.ecCreateExamCenter.examCenter.code,
          },
        });
      },
      onError: (error) => {
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการเพิ่มศูนย์ทดสอบ',
          variant: 'error',
        });

        const validationErrors = transformExamCenterValidationErrors(error);
        for (const validationError of validationErrors) {
          setError(validationError.property, {
            type: 'custom',
            message: validationError.message,
          });
        }
      },
    });
  };

  const handleResetForm = () => {
    reset();
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='sm'
        slotProps={{
          paper: {
            component: Form,
            onSubmit: handleSubmit(handleCreateExamCenter),
          },
          transition: {
            onExited: handleResetForm,
          },
        }}
      >
        <DialogTitle onClose={onClose} loading={isSubmitting}>
          เพิ่มศูนย์ทดสอบ
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
        >
          <ExamCenterDetailsInfoBanner />
          <FormTextField
            name='nameTH'
            label='ชื่อศูนย์ทดสอบ (ภาษาไทย)'
            placeholder='กรุณากรอก ชื่อศูนย์ทดสอบ (ภาษาไทย)'
            required
          />
          <FormTextField
            name='nameEN'
            label='ชื่อศูนย์ทดสอบ (ภาษาอังกฤษ)'
            placeholder='กรุณากรอก ชื่อศูนย์ทดสอบ (ภาษาอังกฤษ)'
            required
          />
          <FormTextField
            name='code'
            label='Exam Center Code'
            placeholder='กรุณากรอก Exam Center Code'
            helperText='กรอกได้สูงสุด 20 ตัวอักษร'
            required
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={isSubmitting}
          >
            ยกเลิก
          </Button>
          <FormButton label='เพิ่มศูนย์ทดสอบ' size='large' />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
